import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingBag, Store, Truck, Coffee, Car, Briefcase, 
  Dumbbell, Hammer, Heart, Dog, ArrowRight, Check, 
  TrendingUp, Users, DollarSign, Clock, Target, Sparkles,
  ChevronRight, Play, Building2, Utensils, Wrench, Scissors
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const IndustriesPage = () => {
  const [activeIndustry, setActiveIndustry] = useState(0);

  const industries = [
    {
      icon: Store,
      name: "Retail & E-commerce",
      tagline: "From 50 SKUs to 5,000",
      color: "from-blue-500 to-blue-600",
      heroStat: "+32%",
      heroLabel: "avg. profit margin increase",
      description: "Whether you're running a Shopify store or managing multiple retail locations, PriceStrike helps you find the sweet spot between competitive pricing and healthy margins.",
      caseStudyRoute: "/case-studies/retail",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800",
      challenges: [
        "Inconsistent pricing across channels (Amazon, website, in-store)",
        "Reacting to competitor price changes too slowly",
        "Leaving money on the table with blanket discounts",
        "No visibility into which products are underpriced"
      ],
      solutions: [
        "Unified pricing strategy across all channels",
        "Real-time competitor monitoring with smart alerts",
        "Product-level discount optimization",
        "Margin opportunity identification"
      ],
      caseStudy: {
        company: "GreenLeaf Home & Garden",
        size: "340 SKUs, 2 locations + Shopify",
        result: "32% profit margin increase in 90 days",
        quote: "We were pricing by gut feel. Now we actually know which products can handle a price increase."
      }
    },
    {
      icon: Truck,
      name: "Wholesale & Distribution",
      tagline: "B2B pricing made simple",
      color: "from-orange-500 to-orange-600",
      heroStat: "80%",
      heroLabel: "less time on pricing tasks",
      description: "Managing customer-specific pricing, volume discounts, and contract renewals is complex. PriceStrike automates the tedious work so you can focus on relationships.",
      caseStudyRoute: "/case-studies/wholesale",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800",
      challenges: [
        "Hundreds of customer-specific price lists to maintain",
        "No consistency in how discounts are applied",
        "Hours spent generating quotes manually",
        "Unclear which accounts are profitable"
      ],
      solutions: [
        "Automated customer tier pricing",
        "Smart discount rules and guardrails",
        "Quote generation in seconds",
        "Account profitability visibility"
      ],
      caseStudy: {
        company: "Pacific Coast Supplies",
        size: "180 B2B accounts, 2,800 SKUs",
        result: "4 hours saved daily on pricing tasks",
        quote: "Our sales team used to spend half their day on pricing. Now they actually sell."
      }
    },
    {
      icon: Coffee,
      name: "Food & Beverage",
      tagline: "From cafes to restaurant chains",
      color: "from-amber-500 to-amber-600",
      heroStat: "+24%",
      heroLabel: "average ticket size",
      description: "Menu pricing is both art and science. PriceStrike helps you optimize prices based on ingredient costs, local competition, and what your customers are actually willing to pay.",
      caseStudyRoute: "/case-studies/food-beverage",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800",
      challenges: [
        "Ingredient costs change but menu prices don't",
        "No idea which menu items are most profitable",
        "Competitors undercutting on key items",
        "Seasonal demand swings catch you off guard"
      ],
      solutions: [
        "Cost-based pricing with margin alerts",
        "Menu engineering insights",
        "Competitive price positioning",
        "Seasonal pricing recommendations"
      ],
      caseStudy: {
        company: "Urban Harvest Kitchen",
        size: "8 locations, farm-to-table concept",
        result: "24% increase in average ticket size",
        quote: "We discovered our signature dish was underpriced by 40%. That single change paid for a year of PriceStrike."
      }
    },
    {
      icon: Wrench,
      name: "Auto Services & Parts",
      tagline: "Parts, service, and repairs",
      color: "from-gray-500 to-gray-600",
      heroStat: "35%",
      heroLabel: "faster inventory turnover",
      description: "Auto parts pricing is a maze of OEM costs, aftermarket alternatives, and customer expectations. PriceStrike helps you price for both DIY customers and professional mechanics.",
      caseStudyRoute: "/case-studies/auto-parts",
      image: "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=800",
      challenges: [
        "Thousands of SKUs with varying demand",
        "Different pricing for retail vs. wholesale",
        "Parts sitting on shelves too long",
        "No visibility into competitive positioning"
      ],
      solutions: [
        "Demand-based inventory pricing",
        "Automated retail/wholesale tier management",
        "Slow-mover identification and repricing",
        "Market-based price positioning"
      ],
      caseStudy: {
        company: "Midwest Auto Supply",
        size: "4,200 SKUs, retail + 45 shop accounts",
        result: "35% faster inventory turnover",
        quote: "We cleared $50K in dead stock just by finding the right price points."
      }
    },
    {
      icon: Briefcase,
      name: "Professional Services",
      tagline: "Agencies, consultants, and firms",
      color: "from-purple-500 to-purple-600",
      heroStat: "+28%",
      heroLabel: "higher project values",
      description: "Pricing services is different from pricing products. PriceStrike helps you structure packages, set rates, and capture the value you deliver.",
      caseStudyRoute: "/case-studies/professional-services",
      image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800",
      challenges: [
        "Undercharging because you don't know market rates",
        "Inconsistent pricing across similar projects",
        "Scope creep eating into margins",
        "No framework for value-based pricing"
      ],
      solutions: [
        "Competitive rate benchmarking",
        "Project pricing templates",
        "Scope-based pricing guardrails",
        "Value-based pricing frameworks"
      ],
      caseStudy: {
        company: "Elevate Digital Agency",
        size: "18 employees, full-service marketing",
        result: "28% higher average project value",
        quote: "We stopped competing on price and started competing on value. Our close rate actually went up."
      }
    },
    {
      icon: Dumbbell,
      name: "Fitness & Wellness",
      tagline: "Studios, gyms, and wellness centers",
      color: "from-pink-500 to-pink-600",
      heroStat: "+45%",
      heroLabel: "membership retention",
      description: "Membership pricing, class packs, and personal training rates all need to work together. PriceStrike helps you find the right balance to maximize recurring revenue.",
      caseStudyRoute: "/case-studies/fitness",
      image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800",
      challenges: [
        "High member churn from pricing friction",
        "Class packs cannibalizing memberships",
        "No clarity on optimal price points",
        "Promotions that attract the wrong customers"
      ],
      solutions: [
        "Membership tier optimization",
        "Class pack pricing strategy",
        "Churn-risk pricing alerts",
        "Promotion ROI tracking"
      ],
      caseStudy: {
        company: "FlowState Yoga Collective",
        size: "6 studio locations",
        result: "45% improvement in membership retention",
        quote: "We were losing members to cheaper options. Turns out our pricing structure was the problem, not our prices."
      }
    },
    {
      icon: Hammer,
      name: "Building & Construction",
      tagline: "Supplies, contractors, and trades",
      color: "from-yellow-600 to-yellow-700",
      heroStat: "+22%",
      heroLabel: "contractor account growth",
      description: "Contractor pricing tiers, volume discounts, and job quotes require precision. PriceStrike automates the complexity so you can focus on the job site.",
      caseStudyRoute: "/case-studies/construction",
      image: "https://images.unsplash.com/photo-1634672652995-ee7525bce595?w=800",
      challenges: [
        "Manual contractor discount tracking",
        "Inconsistent job quotes",
        "Material cost volatility",
        "No loyalty-based pricing framework"
      ],
      solutions: [
        "Automated contractor tier management",
        "Quick job quote generation",
        "Cost pass-through automation",
        "Loyalty pricing rules"
      ],
      caseStudy: {
        company: "Horizon Lumber & Building",
        size: "165 contractor accounts",
        result: "22% increase in contractor accounts",
        quote: "Contractors love knowing exactly what discount they're getting. No more negotiating every order."
      }
    },
    {
      icon: Heart,
      name: "Healthcare & Medical",
      tagline: "Practices, clinics, and providers",
      color: "from-red-500 to-red-600",
      heroStat: "38%",
      heroLabel: "more treatments accepted",
      description: "Self-pay pricing, payment plans, and cash-pay discounts need to be competitive yet profitable. PriceStrike helps you optimize patient pricing without the complexity.",
      caseStudyRoute: "/case-studies/healthcare",
      image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800",
      challenges: [
        "Cash-pay pricing that's out of touch with market",
        "Patients declining treatment due to cost",
        "No framework for payment plan pricing",
        "Inconsistent pricing across locations"
      ],
      solutions: [
        "Market-based cash-pay pricing",
        "Treatment acceptance optimization",
        "Payment plan structure recommendations",
        "Multi-location price harmonization"
      ],
      caseStudy: {
        company: "Bright Smile Dental Group",
        size: "4 practice locations",
        result: "38% more treatment plans accepted",
        quote: "Our cash-pay prices were scaring patients away. Now we're competitive and still profitable."
      }
    }
  ];

  const stats = [
    { value: "8", label: "Industries served" },
    { value: "10 min", label: "Setup time" },
    { value: "14 days", label: "To see first results" },
    { value: "Free", label: "Pricing risk scan" }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      {/* Hero */}
      <section className="pt-40 pb-16 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
            <Building2 className="w-4 h-4 text-[#2563EB]" />
            <span className="text-[#2563EB] font-medium text-sm">Built for Your Industry</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-[#F8FAFC] mb-6">
            Pricing That
            <br />
            <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent">
              Understands Your Business
            </span>
          </h1>
          
          <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto mb-12">
            Every industry has unique pricing challenges. PriceStrike adapts to yours—whether you're managing 50 SKUs or 5,000 customer accounts.
          </p>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            {stats.map((stat, idx) => (
              <div key={idx} className="bg-[#111827]/50 border border-[#2563EB]/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-[#2563EB] mb-1">{stat.value}</div>
                <div className="text-sm text-[#94A3B8]">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Industry Video */}
          <div className="max-w-3xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden border border-[#2563EB]/30 shadow-2xl shadow-[#2563EB]/20">
              <video 
                controls
                className="w-full aspect-video bg-[#0A0F1C]"
                poster="/industry-poster.jpg"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_price-optimizer-12/artifacts/tm0oc9je_Girl%20in%20Suit.mov" 
                  type="video/quicktime" 
                />
                <source 
                  src="https://customer-assets.emergentagent.com/job_price-optimizer-12/artifacts/tm0oc9je_Girl%20in%20Suit.mov" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center text-[#94A3B8] mt-4">How PriceStrike adapts to your industry</p>
          </div>
        </div>
      </section>

      {/* Industry Selector */}
      <section className="py-8 px-6 bg-[#111827] border-y border-[#2563EB]/20 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <button
                  key={index}
                  onClick={() => setActiveIndustry(index)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                    activeIndustry === index
                      ? 'bg-[#2563EB] text-white'
                      : 'bg-[#0A0F1C] text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#0A0F1C]/80'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{industry.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Active Industry Detail */}
      <section className="py-16 px-6 bg-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          {industries.map((industry, index) => {
            if (index !== activeIndustry) return null;
            const Icon = industry.icon;
            
            return (
              <div key={index} className="animate-fadeIn">
                {/* Industry Header */}
                <div className="grid lg:grid-cols-2 gap-12 mb-16">
                  <div>
                    <div className={`inline-flex items-center gap-3 bg-gradient-to-r ${industry.color} rounded-xl px-4 py-2 mb-6`}>
                      <Icon className="w-6 h-6 text-white" />
                      <span className="text-white font-semibold">{industry.name}</span>
                    </div>
                    
                    <h2 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
                      {industry.tagline}
                    </h2>
                    
                    <p className="text-xl text-[#94A3B8] mb-8">
                      {industry.description}
                    </p>
                    
                    <div className="flex gap-4">
                      <Link to="/coming-soon">
                        <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-base font-semibold rounded-lg">
                          Get Started Free
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                      <Link to={industry.caseStudyRoute}>
                        <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 px-8 py-6 text-base font-semibold rounded-lg">
                          View Case Study
                        </Button>
                      </Link>
                    </div>
                  </div>
                  
                  {/* Hero Image & Stat */}
                  <div className="flex items-center justify-center">
                    <div className="relative w-full h-[320px] rounded-3xl overflow-hidden border border-[#2563EB]/30">
                      <img 
                        src={industry.image} 
                        alt={industry.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C] via-[#0A0F1C]/60 to-transparent" />
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className="bg-[#0A0F1C]/80 backdrop-blur-sm rounded-xl p-4 text-center border border-[#2563EB]/20">
                          <div className={`text-5xl font-bold bg-gradient-to-r ${industry.color} bg-clip-text text-transparent mb-1`}>
                            {industry.heroStat}
                          </div>
                          <div className="text-base text-[#94A3B8]">{industry.heroLabel}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Challenges & Solutions */}
                <div className="grid md:grid-cols-2 gap-8 mb-16">
                  {/* Challenges */}
                  <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-red-500/20 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold text-[#F8FAFC] mb-6 flex items-center gap-3">
                      <span className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                        <Target className="w-5 h-5 text-red-400" />
                      </span>
                      Common Challenges
                    </h3>
                    <ul className="space-y-4">
                      {industry.challenges.map((challenge, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-red-500/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="w-2 h-2 bg-red-400 rounded-full" />
                          </span>
                          <span className="text-[#94A3B8]">{challenge}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Solutions */}
                  <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-green-500/20 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold text-[#F8FAFC] mb-6 flex items-center gap-3">
                      <span className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-green-400" />
                      </span>
                      PriceStrike Solutions
                    </h3>
                    <ul className="space-y-4">
                      {industry.solutions.map((solution, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="w-6 h-6 text-green-400 flex-shrink-0" />
                          <span className="text-[#F8FAFC]">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Case Study */}
                <div className="bg-gradient-to-br from-[#2563EB]/10 to-[#111827] border border-[#2563EB]/30 rounded-2xl p-8">
                  <div className="grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                      <p className="text-[#2563EB] font-semibold mb-2">CUSTOMER SUCCESS</p>
                      <h3 className="text-2xl font-bold text-[#F8FAFC] mb-1">{industry.caseStudy.company}</h3>
                      <p className="text-[#94A3B8] mb-4">{industry.caseStudy.size}</p>
                      <blockquote className="text-lg text-[#F8FAFC] italic border-l-4 border-[#2563EB] pl-4 mb-4">
                        "{industry.caseStudy.quote}"
                      </blockquote>
                    </div>
                    <div className="md:col-span-4 text-center">
                      <div className="bg-[#0A0F1C] rounded-xl p-6">
                        <p className="text-sm text-[#94A3B8] mb-1">Result</p>
                        <p className="text-2xl font-bold text-green-400">{industry.caseStudy.result}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* All Industries Quick View */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#F8FAFC] text-center mb-12">
            Quick View: All Industries
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <button
                  key={index}
                  onClick={() => {
                    setActiveIndustry(index);
                    window.scrollTo({ top: 500, behavior: 'smooth' });
                  }}
                  className="bg-[#111827]/50 border border-[#2563EB]/20 rounded-xl p-5 text-left hover:border-[#2563EB]/50 transition-all group"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${industry.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#F8FAFC] mb-1">{industry.name}</h3>
                  <p className="text-sm text-[#94A3B8] mb-2">{industry.tagline}</p>
                  <p className="text-lg font-bold text-[#2563EB]">{industry.heroStat} <span className="text-xs text-[#94A3B8] font-normal">{industry.heroLabel}</span></p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Free Pricing Check Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#2563EB]/20 to-[#111827] border border-[#2563EB]/40 rounded-2xl p-10 text-center">
            <div className="inline-block bg-[#2563EB]/20 text-[#2563EB] text-sm font-semibold px-4 py-2 rounded-full mb-4">
              FREE TOOL — NO SIGNUP
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
              See Your Pricing Risk Score in 3 Minutes
            </h2>
            <p className="text-lg text-[#94A3B8] mb-8 max-w-2xl mx-auto">
              Upload your product data or try with sample products. Find out which of your prices are safe, which are risky, and what to do about it.
            </p>
            <Link to="/pricing-check">
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-lg">
                Run Free Pricing Check
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-sm text-[#94A3B8] mt-4">
              ✓ No credit card &nbsp; ✓ No sales call &nbsp; ✓ Results in minutes
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#0A0F1C]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-6">
            Don't See Your Industry?
          </h2>
          <p className="text-xl text-[#94A3B8] mb-8 max-w-2xl mx-auto">
            PriceStrike works for any business that sells products or services. Try our free pricing check to see how it applies to your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing-check">
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-lg">
                Run Free Pricing Check
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/coming-soon">
              <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 px-10 py-7 text-lg font-semibold rounded-lg">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustriesPage;
